import React, { useState, useMemo } from 'react';
import {
  FileSpreadsheet,
  Headphones,
  FileText,
  Link,
  Cloud,
  Database,
  Zap,
  FolderOpen,
  SlidersHorizontal,
  Search,
  Code,
  MessageSquare,
  Box,
  Code2,
  Plug,
  FileDown,
  ChevronLeft,
  ChevronRight,
  LucideIcon,
} from 'lucide-react';
import { Input } from '../ui/input';
import { nodePaletteItems, categoryLabels, categoryOrder, categoryColors } from '../../config/nodePalette';
import { NodePaletteItem, NodeCategory } from '../../types/flows';

const iconMap: Record<string, LucideIcon> = {
  FileSpreadsheet,
  Headphones,
  FileText,
  Link,
  Cloud,
  Database,
  Zap,
  FolderOpen,
  SlidersHorizontal,
  Search,
  Code,
  MessageSquare,
  Box,
  Code2,
  Plug,
  FileDown,
};

interface NodePaletteProps {
  onAddNode: (type: string, label: string) => void;
}

export const NodePalette: React.FC<NodePaletteProps> = ({ onAddNode }) => {
  const [collapsed, setCollapsed] = useState(() => {
    return typeof window !== 'undefined' && window.innerWidth < 768;
  });
  const [filter, setFilter] = useState('');

  const filtered = useMemo(() => {
    if (!filter.trim()) return nodePaletteItems;
    const q = filter.toLowerCase();
    return nodePaletteItems.filter(
      (item) =>
        item.label.toLowerCase().includes(q) ||
        item.description.toLowerCase().includes(q) ||
        item.category.toLowerCase().includes(q)
    );
  }, [filter]);

  const grouped = useMemo(() => {
    const map = new Map<NodeCategory, NodePaletteItem[]>();
    for (const cat of categoryOrder) {
      const items = filtered.filter((i) => i.category === cat);
      if (items.length > 0) map.set(cat, items);
    }
    return map;
  }, [filtered]);

  const onDragStart = (e: React.DragEvent, item: NodePaletteItem) => {
    e.dataTransfer.setData(
      'application/reactflow',
      JSON.stringify({ type: item.type, label: item.label })
    );
    e.dataTransfer.effectAllowed = 'move';
  };

  if (collapsed) {
    return (
      <div className="w-10 border-r bg-muted/30 flex flex-col items-center pt-2">
        <button
          onClick={() => setCollapsed(false)}
          className="h-8 w-8 rounded-md flex items-center justify-center hover:bg-muted transition-colors"
          title="Expand palette"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <div className="w-[220px] border-r bg-muted/20 flex flex-col shrink-0">
      <div className="flex items-center justify-between px-3 py-2 border-b">
        <span className="text-xs font-semibold">Nodes</span>
        <button
          onClick={() => setCollapsed(true)}
          className="h-6 w-6 rounded flex items-center justify-center hover:bg-muted transition-colors"
          title="Collapse palette"
        >
          <ChevronLeft className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="px-2 py-2">
        <Input
          className="h-7 text-[11px]"
          placeholder="Search nodes..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
      </div>

      <div className="flex-1 overflow-y-auto px-2 pb-2 space-y-3">
        {Array.from(grouped.entries()).map(([category, items]) => (
          <div key={category}>
            <div className="flex items-center gap-1.5 px-1 mb-1">
              <div
                className="h-2 w-2 rounded-full shrink-0"
                style={{ background: categoryColors[category] }}
              />
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
                {categoryLabels[category]}
              </p>
            </div>
            <div className="space-y-1">
              {items.map((item) => {
                const Icon = iconMap[item.icon] || Box;
                return (
                  <div
                    key={item.type}
                    draggable
                    onDragStart={(e) => onDragStart(e, item)}
                    onClick={() => onAddNode(item.type, item.label)}
                    className="flex items-center gap-2 rounded-md px-2 py-1.5 cursor-grab active:cursor-grabbing hover:bg-muted/60 transition-colors border border-transparent hover:border-border"
                    title={item.description}
                  >
                    <Icon className="h-3.5 w-3.5 shrink-0" style={{ color: categoryColors[item.category] }} />
                    <div className="min-w-0">
                      <p className="text-[11px] font-medium leading-tight truncate">
                        {item.label}
                      </p>
                      <p className="text-[9px] text-muted-foreground leading-tight truncate">
                        {item.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
